import { useEffect, useState } from 'react';

/**
 * The world city list, fetched only when something actually needs it.
 *
 * `country-state-city` ships three datasets: countries (96 KB), states (555 KB)
 * and cities — **8.06 MB**. Importing `City` at the top of LeadModal put all of
 * it inside the main bundle, so main.js was 10 MB (2.7 MB gzipped) and EVERY
 * page of the app waited for the whole of it to download and parse before it
 * could render anything. That is the "everything got slow" — nothing to do with
 * the page you were opening; it was the price of admission.
 *
 * Only the Location fields on the lead form ever ask for a city, so the data is
 * now a separate chunk that loads when that form opens. Everywhere else the app
 * starts without it.
 *
 * `City` here is a stand-in with the same shape as the real module, so the call
 * sites read exactly as they did. Before the data lands it answers with an empty
 * list; `useCitiesReady()` re-renders the form the moment it arrives, and the
 * effects that fill the dropdowns run again.
 */

let loaded = null;      // the real City module, once it is here
let pending = null;     // the in-flight import, so it is only fetched once

export const citiesLoaded = () => Boolean(loaded);

export const loadCities = () => {
  if (loaded) return Promise.resolve(loaded);
  if (!pending) {
    // Just the city module: pulling in the package root would drag the country
    // and state lists into this chunk as well, and those are already loaded.
    pending = import(/* webpackChunkName: "city-data" */ 'country-state-city/lib/city')
      .then(mod => { loaded = mod.City || mod.default || mod; return loaded; })
      .catch(err => { pending = null; throw err; });
  }
  return pending;
};

export const City = {
  getCitiesOfState: (countryCode, stateCode) =>
    (loaded ? loaded.getCitiesOfState(countryCode, stateCode) : null) || [],
  getCitiesOfCountry: (countryCode) =>
    (loaded ? loaded.getCitiesOfCountry(countryCode) : null) || [],
  getAllCities: () => (loaded ? loaded.getAllCities() : null) || []
};

/** True once the city list is usable; starts fetching it if it is not. */
export const useCitiesReady = () => {
  const [ready, setReady] = useState(citiesLoaded());
  useEffect(() => {
    if (ready) return undefined;
    let alive = true;
    loadCities().then(() => { if (alive) setReady(true); }).catch(() => {
      // No city list is a smaller problem than a form that will not open: the
      // field still takes anything typed into it.
    });
    return () => { alive = false; };
  }, [ready]);
  return ready;
};
